import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Box, Divider, Typography } from "@mui/material";
import { CloudQueue as CloudIcon } from "@mui/icons-material";

import { Dropzone } from "./components/Dropzone";
import { TableDocuments } from "./components/TableDocuments";
import { NextcloudFilePicker } from "./components/NextcloudFilePicker";
import { SaveLocationPicker } from "../../ui/components/SaveLocationPicker";

import '../../ui/styles/pages/Home.css';
import { GlobalContext } from "../../ui/context/global/GlobalContext";
import { FddApi } from "../../ui/services/server-fdd";

export const Home = () => {
  const { userState: { user } } = useContext(GlobalContext);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [saveLocationOpen, setSaveLocationOpen] = useState(false);
  const [selectedNextcloudFile, setSelectedNextcloudFile] = useState(null);
  const navigate = useNavigate();

  /**
   * Handle file selection from Nextcloud
   */
  const handleNextcloudFileSelect = async (selectedFile) => {
    console.log('[HOME] Selected file from Nextcloud:', selectedFile);
    
    // Store selected file and show save location picker
    setSelectedNextcloudFile(selectedFile);
    setSaveLocationOpen(true);
  };

  /**
   * Handle save location confirmation
   */
  const handleSaveLocationConfirm = async (saveOptions) => {
    try {
      console.log('[HOME] Save options:', saveOptions);

      if (!selectedNextcloudFile) return;

      // Download file from Nextcloud
      const result = await FddApi.downloadNextcloudFile(selectedNextcloudFile.filename);

      if (result.hasError) {
        console.error('[HOME] Error downloading file:', result.message);
        return;
      }

      // Convert blob to File object
      const file = new File([result.blob], result.filename, { type: 'application/pdf' });

      // Prepare Nextcloud metadata
      const nextcloudMetadata = {
        sourcePath: selectedNextcloudFile.filename,
        saveOption: saveOptions.saveOption,
        destinationPath: saveOptions.customPath,
        overwrite: saveOptions.overwrite
      };

      // Create draft with the file and Nextcloud metadata
      const createResult = await FddApi.createDraft(file, nextcloudMetadata);

      if (createResult.hasError) {
        console.error('[HOME] Error creating draft:', createResult.message);
        return;
      }

      console.log('[HOME] Draft created with Nextcloud metadata:', createResult.body);

      // Navigate to draft page
      const hash = createResult.body.hash;
      navigate(`/draft/${hash}`);

    } catch (error) {
      console.error('[HOME] Error handling Nextcloud file:', error);
    } finally {
      setSelectedNextcloudFile(null);
    }
  };

  return (
    <div className="home">
      <section className="home__dropzone">
        <Dropzone />
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          margin: '20px 0',
          gap: 2
        }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            o
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginTop: 2
        }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<CloudIcon />}
            onClick={() => setFilePickerOpen(true)}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              padding: '12px 32px',
              fontSize: '1rem'
            }}
          >
            Seleccionar desde Nextcloud
          </Button>
        </Box>
      </section>

      <section className="history-documents">
        <h2 style={{ fontWeight: "normal" }}>Documentos</h2>
        <br />
        <TableDocuments />
      </section>

      {/* Nextcloud File Picker Dialog */}
      <NextcloudFilePicker
        open={filePickerOpen}
        onClose={() => setFilePickerOpen(false)}
        onSelect={handleNextcloudFileSelect}
        onlyPDFs={true}
        multiSelect={false}
      />

      {/* Save Location Picker Dialog */}
      <SaveLocationPicker
        open={saveLocationOpen}
        onClose={() => {
          setSaveLocationOpen(false);
          setSelectedNextcloudFile(null);
        }}
        onConfirm={handleSaveLocationConfirm}
        sourcePath={selectedNextcloudFile?.filename || ''}
      />
    </div>
  );
};